import React from 'react';
import CartIcon from '../assets/CartIcon.jsx';
import { captureException, captureMessage, startTransaction } from '@sentry/react';
import TradePayCodes from './Constants/TradePayCodes.js';
import { SentryConfig } from './Constants/Sentry.js';
import RecurringPaymentCodes from './Constants/RecurringPaymentCodes.js';
import { defaultAnalyticsVariables, events, pagePrefix } from './Constants/Analytics.js';
import useNormalizedSubscriptionStatus from '../services/utils/subscriptionUtils.js';

const defaultSnackResponse = {
  icon: <CartIcon />,
  body: 'Something went wrong with payment',
};
const systemError = 'system error';

export const handlePaymentError = (resultCode, setSnackBar) => {
  const subscriptionStatus = useNormalizedSubscriptionStatus();
  const paymentErrorTransaction = startTransaction(SentryConfig.payments.gatewayErrors.transaction);
  switch (resultCode) {
    case TradePayCodes.CANCELLED: {
      captureMessage(`Payment Gateway response - ${TradePayCodes.CANCELLED}: User cancelled payment request`, 'info');
      break;
    }

    case TradePayCodes.FAILED: {
      setSnackBar({
        icon: <CartIcon />,
        body: 'Payment failed',
      });
      window.utag?.view({
        ...defaultAnalyticsVariables,
        event_name: [events.error],
        page_name: `${pagePrefix}: error handler`,
        page_error_name: 'Payment failed. Due to trade pay codes',
        page_error_type: systemError,
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
      });

      break;
    }

    default: {
      captureException(new Error(`Unexpected Payment Gateway result code: ${resultCode}`));
      setSnackBar(defaultSnackResponse);
      window.utag?.view({
        ...defaultAnalyticsVariables,
        event_name: [events.error],
        page_name: `${pagePrefix}: error handler`,
        page_error_name: 'Payment failed. Due to unexpected result code',
        page_error_type: systemError,
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
      });
      break;
    }
  }

  paymentErrorTransaction.finish();
};

export const handleAdvanceError = (responseCode, setSnackBar) => {
  const subscriptionStatus = useNormalizedSubscriptionStatus();
  switch (responseCode) {
    case RecurringPaymentCodes.CANCELLED: {
      captureMessage(`Recurring Payment response - ${RecurringPaymentCodes.CANCELLED}: User cancelled payment request`, 'info');
      window.utag?.view({
        ...defaultAnalyticsVariables,
        event_name: [events.error],
        page_name: `${pagePrefix}: error handler`,
        page_error_name: 'Payment failed. Due to user cancelled payment request',
        page_error_type: 'user error',
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
      });
      break;
    }

    case RecurringPaymentCodes.SUS_FRAUD: {
      captureMessage(`Recurring Payment response - ${RecurringPaymentCodes.SUS_FRAUD}: Suspected Fraud`, 'warning');
      setSnackBar(defaultSnackResponse);
      window.utag?.view({
        ...defaultAnalyticsVariables,
        event_name: [events.error],
        page_name: `${pagePrefix}: error handler`,
        page_error_name: 'Payment failed. Due to suspected fraud',
        page_error_type: systemError,
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
      });
      break;
    }

    default: {
      captureException(new Error(`Unexpected Recurring Payment response code: ${responseCode}`));
      setSnackBar(defaultSnackResponse);
      window.utag?.view({
        ...defaultAnalyticsVariables,
        event_name: [events.error],
        page_name: `${pagePrefix}: error handler`,
        page_error_name: 'Payment failed. Due to unexpected Recurring Payment response code',
        page_error_type: 'user error',
        visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatus}`,
      });
      break;
    }
  }
};
